// Expenses: money going out. Money is ALWAYS integer ngwee here.
//
// The rules (who may approve, what freezes when, when an expense counts as spent) live in
// the database (0010_approval_engine.sql). These functions ask; the database answers, and
// its plain-language refusals are shown as written.
import { supabase } from '@/lib/supabase'
import { currentUserId } from './auth'
import { DataError, unwrap } from './errors'
import type { PaymentMethod } from './dues'

export type ExpenseStatus = 'draft' | 'submitted' | 'approved' | 'paid' | 'rejected' | 'void'
export type PaidFrom = 'department_cash' | 'central_treasury'

export const PAID_FROM_LABELS: Record<PaidFrom, string> = {
  department_cash: 'Department cash',
  central_treasury: 'Paid by central treasury',
}

export interface Expense {
  id: string
  departmentId: string
  budgetLineId: string | null
  budgetLineName: string | null
  categoryId: string
  categoryName: string
  payee: string
  description: string
  amountNgwee: number
  incurredOn: string
  paidOn: string | null
  paidFrom: PaidFrom
  method: PaymentMethod
  receiptNo: string | null
  status: ExpenseStatus
  requiresApproval: boolean | null
  decisionNote: string | null
  /** Set on the negative entry that corrects an earlier expense. */
  reversesId: string | null
  reversalReason: string | null
  clientRef: string | null
}

type Row = {
  id: string
  department_id: string
  budget_line_id: string | null
  category_id: string
  payee: string
  description: string
  amount_ngwee: number
  incurred_on: string
  paid_on: string | null
  paid_from: PaidFrom
  method: PaymentMethod
  receipt_no: string | null
  status: ExpenseStatus
  requires_approval: boolean | null
  decision_note: string | null
  reverses_id: string | null
  reversal_reason: string | null
  client_ref: string | null
  category: { name: string } | null
  line: { description: string } | null
}

// Embeds go by TABLE name (see CLAUDE.md): budget_line is joined through a two-column key.
const COLS =
  'id, department_id, budget_line_id, category_id, payee, description, amount_ngwee, incurred_on, paid_on, paid_from, method, receipt_no, status, requires_approval, decision_note, reverses_id, reversal_reason, client_ref, category:expense_category(name), line:budget_line(description)'

function toExpense(r: Row): Expense {
  return {
    id: r.id,
    departmentId: r.department_id,
    budgetLineId: r.budget_line_id,
    budgetLineName: r.line?.description ?? null,
    categoryId: r.category_id,
    categoryName: r.category?.name ?? '',
    payee: r.payee,
    description: r.description,
    amountNgwee: Number(r.amount_ngwee),
    incurredOn: r.incurred_on,
    paidOn: r.paid_on,
    paidFrom: r.paid_from,
    method: r.method,
    receiptNo: r.receipt_no,
    status: r.status,
    requiresApproval: r.requires_approval,
    decisionNote: r.decision_note,
    reversesId: r.reverses_id,
    reversalReason: r.reversal_reason,
    clientRef: r.client_ref,
  }
}

/** The department's expenses, newest first; optionally one quarter only. */
export async function listExpenses(departmentId: string, quarterId?: string): Promise<Expense[]> {
  let q = supabase.from('expense').select(COLS).eq('department_id', departmentId)
  if (quarterId) q = q.eq('quarter_id', quarterId)
  const res = await q.order('incurred_on', { ascending: false }).order('created_at', { ascending: false })
  return (unwrap(res as never, 'Could not load the expenses.') as Row[]).map(toExpense)
}

export interface SpendableLine {
  id: string
  description: string
  categoryId: string
  categoryName: string
  budgetedNgwee: number
  remainingNgwee: number
}

/** Lines of APPROVED budgets for the quarter an expense falls in (the only lines it may be charged to). */
export async function listSpendableLines(departmentId: string, quarterId: string): Promise<SpendableLine[]> {
  const res = await supabase
    .from('v_budget_line_actuals')
    .select('budget_line_id, description, category_id, category_name, budgeted_ngwee, remaining_ngwee')
    .eq('department_id', departmentId)
    .eq('quarter_id', quarterId)
    .eq('budget_status', 'approved')
    .order('category_name')
    .order('description')
  return (
    unwrap(res as never, 'Could not load the budget lines.') as {
      budget_line_id: string
      description: string
      category_id: string
      category_name: string
      budgeted_ngwee: number
      remaining_ngwee: number
    }[]
  ).map((r) => ({
    id: r.budget_line_id,
    description: r.description,
    categoryId: r.category_id,
    categoryName: r.category_name,
    budgetedNgwee: Number(r.budgeted_ngwee),
    remainingNgwee: Number(r.remaining_ngwee),
  }))
}

export interface ExpenseInput {
  /** Null means spending that is not in the budget ("unbudgeted"). */
  budgetLineId: string | null
  categoryId: string
  payee: string
  description: string
  amountNgwee: number
  incurredOn: string
  paidFrom: PaidFrom
  method: PaymentMethod
  receiptNo?: string
}

const fields = (e: ExpenseInput) => ({
  budget_line_id: e.budgetLineId,
  category_id: e.categoryId,
  payee: e.payee.trim(),
  description: e.description.trim(),
  amount_ngwee: e.amountNgwee,
  incurred_on: e.incurredOn,
  paid_from: e.paidFrom,
  method: e.method,
  receipt_no: e.receiptNo?.trim() ? e.receiptNo.trim() : null,
})

/**
 * Save a new expense as a draft and return its id. `clientRef` makes a retry (double tap, lost
 * connection) return the same row instead of recording the expense twice.
 */
export async function createExpense(departmentId: string, e: ExpenseInput, clientRef: string): Promise<string> {
  if (!Number.isSafeInteger(e.amountNgwee) || e.amountNgwee <= 0) {
    throw new DataError({ code: 'P0001', message: 'The amount must be greater than zero.' })
  }
  const userId = await currentUserId()
  const res = await supabase
    .from('expense')
    .insert({ department_id: departmentId, created_by: userId, client_ref: clientRef, ...fields(e) })
    .select('id')
    .single()
  if (res.error) {
    if (res.error.code === '23505') {
      const prior = await supabase
        .from('expense')
        .select('id')
        .eq('department_id', departmentId)
        .eq('client_ref', clientRef)
        .maybeSingle()
      if (prior.data) {
        // The first try reached the server but its answer was lost. The person may have corrected
        // the form since, so what is saved must be what is on the screen now, not the first values.
        const id = (prior.data as { id: string }).id
        await supabase.from('expense').update(fields(e)).eq('id', id).eq('status', 'draft')
        return id
      }
    }
    throw new DataError(res.error, 'Could not save the expense.')
  }
  return (res.data as { id: string }).id
}

/** Change a draft (the database refuses once it has been sent). */
export async function updateDraftExpense(id: string, e: ExpenseInput): Promise<void> {
  if (!Number.isSafeInteger(e.amountNgwee) || e.amountNgwee <= 0) {
    throw new DataError({ code: 'P0001', message: 'The amount must be greater than zero.' })
  }
  const res = await supabase.from('expense').update(fields(e)).eq('id', id).eq('status', 'draft').select('id')
  if (res.error) throw new DataError(res.error, 'Could not save the expense.')
  if (((res.data as unknown[]) ?? []).length === 0) throw staleError('Could not save the expense.')
}

export async function deleteDraftExpense(id: string): Promise<void> {
  const res = await supabase.from('expense').delete().eq('id', id).eq('status', 'draft').select('id')
  if (res.error) throw new DataError(res.error, 'Could not discard the draft.')
  if (((res.data as unknown[]) ?? []).length === 0) throw staleError('Could not discard the draft.')
}

const staleError = (fallback: string) =>
  new DataError(
    { code: 'P0001', message: 'This expense has changed since you opened it. Refresh the page and try again.' },
    fallback,
  )

async function setStatus(
  id: string,
  from: ExpenseStatus[],
  patch: Record<string, unknown>,
  fallback: string,
): Promise<void> {
  const res = await supabase.from('expense').update(patch).eq('id', id).in('status', from).select('id')
  if (res.error) throw new DataError(res.error, fallback)
  if (((res.data as unknown[]) ?? []).length === 0) throw staleError(fallback)
}

/** Send a draft. `paidOn` is stated now when the cash has already gone out (then it counts as paid once approved). */
export const submitExpense = (id: string, paidOn: string | null) =>
  setStatus(id, ['draft'], { status: 'submitted', paid_on: paidOn }, 'Could not send the expense.')
/** Take back an expense that has not been decided yet; it becomes an editable draft again. */
export const recallExpense = (id: string) =>
  setStatus(id, ['submitted'], { status: 'draft' }, 'Could not take the expense back.')
/** After a rejection: back to a draft that can be corrected and sent again. */
export const reopenRejectedExpense = (id: string) =>
  setStatus(id, ['rejected'], { status: 'draft' }, 'Could not reopen the expense.')
export const approveExpense = (id: string) =>
  setStatus(id, ['submitted'], { status: 'approved' }, 'Could not approve the expense.')
export const rejectExpense = (id: string, reason: string) =>
  setStatus(id, ['submitted'], { status: 'rejected', decision_note: reason.trim() }, 'Could not reject the expense.')
/** Central treasury: take back an approval on an expense that has NOT been paid yet. */
export const withdrawApproval = (id: string, reason: string) =>
  setStatus(id, ['approved'], { status: 'rejected', decision_note: reason.trim() }, 'Could not withdraw the approval.')
/** The cash has now left: record the date (only on an approved expense). */
export const markExpensePaid = (id: string, paidOn: string) =>
  setStatus(id, ['approved'], { status: 'paid', paid_on: paidOn }, 'Could not mark the expense as paid.')

/** Central treasury only: correct a PAID expense with a visible negative entry. Nothing is deleted. */
export async function reverseExpense(id: string, reason: string): Promise<void> {
  const res = await supabase.rpc('reverse_expense', { p_id: id, p_reason: reason.trim() })
  if (res.error) throw new DataError(res.error, 'Could not reverse the expense.')
}

/** Expenses waiting for a decision, across the church (central treasury's queue). */
export async function listWaitingExpenses(): Promise<(Expense & { departmentName: string })[]> {
  const res = await supabase
    .from('expense')
    .select(`${COLS}, department:department(name)`)
    .eq('status', 'submitted')
    .order('submitted_at')
  return (unwrap(res as never, 'Could not load the expenses waiting for approval.') as (Row & { department: { name: string } | null })[]).map(
    (r) => ({ ...toExpense(r), departmentName: r.department?.name ?? '' }),
  )
}
